// EXTERNAL DEPENDENCIES
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

//COMPONENTS
import { BodyText, HeadingText, Button, Row } from '@paypalcorp/pp-react'

//STYLES
import {
  CriticalIcon,
  ErrorContainer,
  HeadingRow,
  TextContainer,
  TextRow,
} from './styles'

//HELPERS
import { useBusinessProfilePageContent } from '../business-profile-page/hooks'
import { recordImpression } from '../../utils/fpti'

//CONSTANTS
import { ERROR_PAGE_CONTAINER_TEST_ID } from './constants/test-ids'
import { fptiMetadata } from './constants/fpti-constants'

type ErrorPageProps = {
  profileDataNotFound?: boolean
  orderCaptureFailed?: boolean
  reloadPage?: boolean
}

/**
 * Displays a error page to go back or retry/refresh the page
 * @param {object} props
 * @param {boolean} props.profileDataNotFound - if true, will render error messages for profile data not found
 * @param {boolean} props.reloadPage - if true, will render Try Again button, else render Go back button
 */
export default function ErrorPage({
  profileDataNotFound = false,
  orderCaptureFailed = false,
  reloadPage = true,
}: Readonly<ErrorPageProps>) {
  const useContent = useBusinessProfilePageContent()
  const navigate = useNavigate()

  //contents
  const noProfileHeading = useContent('profileNotFoundHeading')
  const noProfileTextLine = useContent('profileNotFoundTextLine')
  const orderCaptureFailedHeading = useContent('orderCaptureFailedHeading')
  const orderCaptureFailedTextLine = useContent('orderCaptureFailedTextLine')
  const heading = useContent('errorPageHeading')
  const textLine1 = useContent('errorPageTextLine1')
  const textLine2 = useContent('errorPageTextLine2')
  const goBackBtn = useContent('errorPageGoBackBtn')
  const tryAgainBtn = useContent('errorPageTryAgainBtn')

  const [reload, setReload] = useState(reloadPage)
  useEffect(() => {
    //identify and go back to the webpage from where the link was clicked
    const referral = document.referrer
    if (referral && referral !== window.location.href && profileDataNotFound) {
      setReload(false)
    }
  }, [profileDataNotFound])

  useEffect(() => {
    const eventName = profileDataNotFound
      ? fptiMetadata.NO_PROFILE_EVENT
      : fptiMetadata.GENERIC_ERROR_EVENT
    const page = profileDataNotFound
      ? fptiMetadata.NO_PROFILE_ERROR_PAGE
      : fptiMetadata.GENERIC_ERROR_PAGE
    recordImpression({
      page,
      eventName,
      payerId: null,
      handle: null,
    })
  }, [profileDataNotFound])

  const handleClick = () => (reload ? navigate(0) : navigate(-1)) //button click action - reload page or go back to previous page

  const getHeading = () => {
    if (profileDataNotFound) return noProfileHeading
    if (orderCaptureFailed) return orderCaptureFailedHeading
    return heading
  }

  const getTextLine = () => {
    if (profileDataNotFound) return noProfileTextLine
    if (orderCaptureFailed) return orderCaptureFailedTextLine
    return textLine1
  }

  const getTextLine2 = () => {
    if (profileDataNotFound || orderCaptureFailed) return null
    return textLine2
  }

  return (
    <ErrorContainer data-testid={ERROR_PAGE_CONTAINER_TEST_ID}>
      <Row>
        <CriticalIcon color='sysColorAccent2Main' />
      </Row>
      <TextContainer>
        <HeadingRow>
          <HeadingText size='sm'>{getHeading()}</HeadingText>
        </HeadingRow>
        <TextRow>
          <BodyText>{getTextLine()}</BodyText>
          <BodyText>{getTextLine2()}</BodyText>
        </TextRow>
      </TextContainer>
      <Button onClick={handleClick} fullWidthOnMobile={false}>
        {reload ? tryAgainBtn : goBackBtn}
      </Button>
    </ErrorContainer>
  )
}
